import type { ReactNode } from "react";
import { cx } from "../cx";

/** Shared label/hint/error frame for TextInput, TextArea, Select. */
export function Field({
  id,
  label,
  hint,
  error,
  children,
  className = "",
}: {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div data-component="Field" className={cx("flex flex-col gap-1", className)}>
      {label ? (
        <label htmlFor={id} className="label-mono">
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-data text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-data text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface FieldState {
  /** True when Field renders the error paragraph, so the red frame and `aria-invalid` cannot disagree with it. */
  readonly invalid: boolean;
  /** The id of the paragraph Field actually renders, or undefined when it renders neither. */
  readonly describedBy: string | undefined;
}

/**
 * The one predicate the frame and its control share. Field renders the error on
 * plain truthiness, so `error=""` from `form.errors.title ?? ""` or a
 * translation that resolves empty renders the hint, and a control computing
 * `invalid` any other way would draw a red border and point `aria-describedby`
 * at an `-error` paragraph that is not in the document. TextInput, TextArea and
 * Select all read this, so a fourth control cannot forget the association.
 */
export function fieldState(id: string, error: ReactNode, hint: ReactNode): FieldState {
  const invalid = Boolean(error);
  return { invalid, describedBy: invalid ? `${id}-error` : hint ? `${id}-hint` : undefined };
}

/**
 * The frame every control shares. `text` is a parameter rather than something
 * an `extra` overrides, because two Tailwind size utilities on one element are
 * decided by their order in the generated stylesheet, not by the order in the
 * class attribute: a control passing `text-book` in `extra` silently kept
 * `text-ui`, which is what made TextArea's `serif` variant and TextInput's
 * `mono` variant the wrong size.
 */
export const controlClass = (invalid: boolean, extra = "", text = "text-ui") =>
  cx(
    "w-full rounded-sm border bg-surface px-2.5 text-ink outline-none placeholder:text-ink-3 focus:border-accent disabled:bg-sunken disabled:text-ink-3",
    text,
    invalid ? "border-danger" : "border-line",
    extra,
  );
